import React from 'react'
import { IconButton, makeStyles, Tooltip } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import { NAME_RE } from '../shared/judgeSpec'
import { useEvalRunForm } from './EvalRunFormContext'

const useStyles = makeStyles(() => ({
  table: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden',
    backgroundColor: 'white'
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr 90px 1.6fr 60px 28px',
    gap: '8px',
    padding: '6px 8px',
    backgroundColor: 'var(--color-neutral-050)',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    alignItems: 'center'
  },
  weightHead: {
    textAlign: 'right'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 90px 1.6fr 60px 28px',
    gap: '8px',
    padding: '4px 8px',
    borderBottom: '1px solid var(--color-neutral-100)',
    alignItems: 'center',
    '&:last-child': { borderBottom: 'none' }
  },
  cellInput: {
    width: '100%',
    padding: '4px 6px',
    border: '1px solid transparent',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    fontFamily: 'var(--font-mono, monospace)',
    outline: 'none',
    backgroundColor: 'transparent',
    color: 'var(--color-neutral-900)',
    boxSizing: 'border-box',
    '&:hover': { borderColor: 'var(--color-neutral-200)' },
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      backgroundColor: 'white',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  cellInputText: {
    fontFamily: 'inherit'
  },
  cellSelect: {
    width: '100%',
    padding: '3px 4px',
    border: '1px solid transparent',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    outline: 'none',
    backgroundColor: 'transparent',
    color: 'var(--color-neutral-900)',
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&:hover': { borderColor: 'var(--color-neutral-200)' },
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      backgroundColor: 'white',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  cellInputNumber: {
    textAlign: 'right'
  },
  nameInvalid: {
    color: 'var(--color-danger-600)',
    borderColor: 'var(--color-danger-300, #fca5a5)'
  },
  removeBtn: {
    padding: 2,
    color: 'var(--color-neutral-300)',
    '&:hover': {
      color: 'var(--color-danger-600, #dc2626)',
      backgroundColor: 'var(--color-danger-050, #fef2f2)'
    }
  },
  addRow: {
    padding: '4px 8px',
    borderTop: '1px solid var(--color-neutral-100)',
    display: 'flex',
    alignItems: 'center'
  },
  addBtn: {
    padding: 2,
    color: 'var(--color-neutral-400)',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  },
  addLabel: {
    fontSize: '11px',
    color: 'var(--color-neutral-500)',
    marginLeft: '4px'
  },
  iconSm: { fontSize: 14 },
  iconMd: { fontSize: 16 }
}))

function MetricsTable () {
  const classes = useStyles()
  const { metrics, updateMetric, removeMetric, addMetric } = useEvalRunForm()

  return (
    <div className={classes.table}>
      <div className={classes.header}>
        <div>Metric</div>
        <div>Type</div>
        <div>Description</div>
        <div className={classes.weightHead}>Weight</div>
        <div />
      </div>
      {metrics.map(m => {
        const nameOk = !m.name || NAME_RE.test(m.name)
        return (
          <div key={m.id} className={classes.row}>
            <input
              className={`${classes.cellInput} ${!nameOk ? classes.nameInvalid : ''}`}
              value={m.name}
              placeholder='metric_name'
              onChange={(e) => updateMetric(m.id, { name: e.target.value })}
            />
            <select
              className={classes.cellSelect}
              value={m.type}
              onChange={(e) => updateMetric(m.id, { type: e.target.value })}
            >
              <option value='passfail'>pass/fail</option>
              <option value='rating'>rating</option>
            </select>
            <input
              className={`${classes.cellInput} ${classes.cellInputText}`}
              value={m.description}
              placeholder='What is being measured?'
              onChange={(e) => updateMetric(m.id, { description: e.target.value })}
            />
            <input
              type='number'
              step={0.5}
              className={`${classes.cellInput} ${classes.cellInputNumber}`}
              value={m.weight}
              onChange={(e) => updateMetric(m.id, { weight: e.target.value === '' ? '' : Number(e.target.value) })}
            />
            <Tooltip title='Remove' arrow>
              <IconButton
                size='small'
                className={classes.removeBtn}
                onClick={() => removeMetric({ id: m.id })}
              >
                <CloseIcon className={classes.iconSm} />
              </IconButton>
            </Tooltip>
          </div>
        )
      })}
      <div className={classes.addRow}>
        <Tooltip title='Add metric' arrow>
          <IconButton size='small' className={classes.addBtn} onClick={addMetric}>
            <AddIcon className={classes.iconMd} />
          </IconButton>
        </Tooltip>
        <span className={classes.addLabel}>Add metric</span>
      </div>
    </div>
  )
}

export default MetricsTable
